import { Subhead } from "../components/SettingsLayout";
import {
  AlertFillIcon,
  CheckCircleFillIcon,
  ClockIcon,
  DatabaseIcon,
  GitBranchIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  HistoryIcon,
  InfoIcon,
  PlayIcon,
  PulseIcon,
  RepoPushIcon,
  StopwatchIcon,
  SyncIcon,
  XIcon,
  type Icon as OcticonIcon,
} from "@primer/octicons-react";
import { rp } from "../api/client";
import type { ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useChanges, useRepo, useRemote, useStats } from "../api/hooks";
import { openCount, type RepoInfo, type RepoStats } from "../api/types";
import { BeeMascot, type BeeState } from "../components/BeeMascot";
import { Avatar, Empty, Query, Sha, Stat, StatsRow, TimeAgo } from "../components/common";
import { forge } from "../components/forge";
import { duration } from "../components/format";
import { Badge, Counter } from "../components/ui/badge";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { List, ListItem, ListItemMeta, ListItemSeparator, ListItemTitle } from "../components/ui/list";
import { Select } from "../components/ui/select";
import { Tooltip } from "../components/ui/tooltip";
import { turnCounts } from "../lib/supervise";
import { cn } from "../lib/utils";
import { ChangeRow } from "./Changes";
import { BatchLabel, PrLink } from "./Remote";

const WINDOWS = [
  { value: "1", label: "Last hour" },
  { value: "24", label: "Last 24 hours" },
  { value: "168", label: "Last 7 days" },
  { value: "720", label: "Last 30 days" },
];

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const pct = (r: number | null) => (r === null ? "–" : `${Math.round(r * 100)}%`);

/** Rates at or above `warn` / `bad` are coloured; zero and "no data" stay neutral. */
function rateTone(r: number | null, warn: number, bad: number) {
  if (!r) return undefined;
  return r >= bad ? "text-danger" : r >= warn ? "text-warning" : undefined;
}

const denseCard =
  "mt-3 [&_[data-slot=card-header]]:min-h-10 [&_[data-slot=card-header]]:px-3 [&_[data-slot=card-header]]:py-1.5 [&_[data-slot=card-header]]:text-sm";
const denseList =
  "[&_[data-slot=list-item]]:gap-2 [&_[data-slot=list-item]]:px-3 [&_[data-slot=list-item]]:py-1.5 [&_[data-slot=list-item-title]]:text-sm [&_[data-slot=list-item-meta]]:mt-0.5";
const denseStats = "[&>div]:flex-[1_1_120px] [&>div]:gap-2 [&>div]:px-3 [&>div]:py-2 [&_.text-xl]:text-lg";
/** Stats laid flush inside a card: the card already draws the frame. */
const cardStats = cn("mt-0 rounded-none border-0", denseStats);

/** The repo at a glance for humans: is main clean, and what are the agents doing. */
export function Overview() {
  const { repo = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const hours = WINDOWS.some((w) => w.value === params.get("hours")) ? Number(params.get("hours")) : 24;
  const stats = useStats(repo, hours);
  const info = useRepo(repo).data?.info;
  return (
    <Query q={stats}>
      {(s) => (
        <>
          <Subhead title="Overview">How main is doing, what needs you, and how agents have landed lately.</Subhead>
          <Health repo={repo} s={s} info={info} />
          <NeedsYou repo={repo} landed={s.throughput.landed} hours={hours} />
          <div className="mt-4 flex items-center gap-2">
            <h2 className="text-sm font-semibold">Activity</h2>
            <Select
              size="sm"
              align="end"
              className="ml-auto w-auto"
              value={String(hours)}
              onValueChange={(v) => setParams(v === "24" ? {} : { hours: v })}
              options={WINDOWS}
              aria-label="Time window"
            />
          </div>
          <Numbers repo={repo} s={s} info={info} />
          <Lands s={s} />
          <Coordination repo={repo} s={s} />
          <Cost repo={repo} s={s} />
          <RemoteCard repo={repo} />
          <InFlight repo={repo} />
        </>
      )}
    </Query>
  );
}

function Health({ repo, s, info }: { repo: string; s: RepoStats; info?: RepoInfo }) {
  const m = s.main;
  const v = m.verification;
  const head = info?.main?.commit_sha;
  const verifyLink = <Link to={`${rp(repo)}/ci?view=verify`}>verification history</Link>;
  const banner: Record<
    RepoStats["main"]["status"],
    { variant: "success" | "info" | "warning" | "error"; Icon: OcticonIcon; spin?: boolean; title: string; body: ReactNode }
  > = {
    verified: {
      variant: "success",
      Icon: CheckCircleFillIcon,
      title: "Main is clean",
      body: v ? (
        <>
          Every landed change passed post-land verification
          {m.last_verified_at ? (
            <>
              , last checked <TimeAgo ms={m.last_verified_at} />
            </>
          ) : null}
          .
        </>
      ) : (
        "Nothing has landed that needs verification yet."
      ),
    },
    verifying: {
      variant: "info",
      Icon: SyncIcon,
      spin: true,
      title: "Verifying main",
      body: (
        <>
          CI is rerunning on main for {plural(m.unverified, "landed commit")} ({v?.phase ?? "head"} phase). See {verifyLink}.
        </>
      ),
    },
    pending: {
      variant: "info",
      Icon: ClockIcon,
      title: `${plural(m.unverified, "landed commit")} awaiting verification`,
      body: (
        <>
          Each change passed its own CI; the next verification reruns CI on main to catch interactions between them.
          {m.last_verified_at ? (
            <>
              {" "}
              Last verified <TimeAgo ms={m.last_verified_at} />.
            </>
          ) : null}
        </>
      ),
    },
    failing: {
      variant: "error",
      Icon: AlertFillIcon,
      title: "Main needs a human",
      body: <>The last verification failed without a single culprit, so the paths it touched are frozen. See {verifyLink}.</>,
    },
    empty: { variant: "info", Icon: InfoIcon, title: "Main is empty", body: "Nothing has landed yet." },
  };
  const b = banner[m.status];
  const conflicts = info?.counts.conflict ?? 0;
  const notes = [
    info?.frozen && (
      <Link key="frozen" to={`${rp(repo)}/settings/policy`}>
        Landing is frozen
      </Link>
    ),
    !info?.frozen && info?.frozen_paths.length ? (
      <span key="paths">
        Frozen paths: <code>{info.frozen_paths.join(", ")}</code>
      </span>
    ) : null,
    conflicts > 0 && (
      <Link key="conflicts" to={`${rp(repo)}/ci?state=conflict`}>
        {plural(conflicts, "change")} in conflict
      </Link>
    ),
    s.health.review.waiting > 0 && (
      <Link key="review" to={`${rp(repo)}/ci?state=review`}>
        {plural(s.health.review.waiting, "change")} waiting for review
      </Link>
    ),
    m.quarantined > 0 && (
      <Link key="quarantined" to={`${rp(repo)}/ci?view=runs`}>
        {plural(m.quarantined, "quarantined job")}
      </Link>
    ),
    v?.state === "reverted" && (
      <span key="reverted">
        Last verification reverted a culprit <TimeAgo ms={v.updated} />
      </span>
    ),
  ].filter(Boolean);
  const tone = HEALTH_TONE[b.variant];
  return (
    <Card>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={cn("flex size-10 shrink-0 items-center justify-center rounded-full", tone.disc)}>
          <b.Icon size={20} className={cn(b.spin && "animate-spin")} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold leading-tight">{b.title}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{b.body}</p>
        </div>
        {head && (
          <div className="flex shrink-0 items-center gap-1 rounded-md border bg-muted py-0.5 pl-2 pr-1 text-xs">
            <GitBranchIcon size={14} className="text-muted-foreground" />
            <span className="font-semibold">main</span>
            <Sha repo={repo} sha={head} n={7} />
          </div>
        )}
      </div>
      {notes.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t bg-muted px-4 py-1.5 text-xs">
          <AlertFillIcon size={12} className="text-warning" />
          {notes}
        </div>
      )}
    </Card>
  );
}

/** Whose turn it is, as cards on Overview. Other repo pages do not repeat it. */
function NeedsYou({ repo, landed, hours }: { repo: string; landed: number; hours: number }) {
  const open = useChanges(repo, "open");
  if (!open.data) return null;
  const { you, agent, working } = turnCounts(open.data.changes);
  const quiet = !you && !agent && !working;
  const bee: BeeState = agent ? "failed" : you ? "review" : working ? "working" : "idle";
  const parts = [
    you > 0 ? `${plural(you, "change")} ${you === 1 ? "needs" : "need"} you` : "",
    agent > 0 ? `${plural(agent, "change")} ${agent === 1 ? "is" : "are"} the agent's turn` : "",
    working > 0 ? `${plural(working, "agent")} working` : "",
  ].filter(Boolean);
  const title = quiet ? "All clear" : parts[0];
  const line = quiet ? "Nothing needs you and no agent is working right now." : parts.slice(1).join(" · ") || "Nothing else is waiting.";
  const count = (n: number, to: string) => (n > 0 ? <Link to={to}>{n}</Link> : <span className="text-muted-foreground/60">0</span>);
  return (
    <Card className={denseCard}>
      <div className="flex items-center gap-3 px-4 py-3">
        <BeeMascot state={bee} cell={2} className="max-sm:hidden" />
        <div className="min-w-0">
          <h2 className="text-sm font-semibold leading-tight">{title}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{line}</p>
        </div>
      </div>
      <StatsRow className={cn(cardStats, "[&>div]:border-l-0")}>
        <Stat n={count(you, `${rp(repo)}/ci`)} label={you === 1 ? "needs you" : "need you"} icon={<AlertFillIcon size={16} />} />
        <Stat n={count(agent, `${rp(repo)}/ci`)} label={agent === 1 ? "is the agent's turn" : "are the agent's turn"} icon={<SyncIcon size={16} />} />
        <Stat n={count(working, `${rp(repo)}/ci`)} label={working === 1 ? "agent working" : "agents working"} icon={<PulseIcon size={16} />} />
        <Stat n={count(landed, `${rp(repo)}/ci?state=landed`)} label={hours === 24 ? "landed today" : "landed"} icon={<GitMergeIcon size={16} />} />
      </StatsRow>
    </Card>
  );
}

const HEALTH_TONE: Record<"success" | "info" | "warning" | "error", { disc: string }> = {
  success: { disc: "bg-success-subtle text-success" },
  info: { disc: "bg-info-subtle text-info" },
  warning: { disc: "bg-warning-subtle text-warning" },
  error: { disc: "bg-danger-subtle text-danger" },
};

function Numbers({ repo, s, info }: { repo: string; s: RepoStats; info?: RepoInfo }) {
  const t = s.throughput;
  const judged = s.ci.pass + s.ci.fail;
  const working = s.agents.filter((a) => a.open > 0).length;
  const passRate = judged ? s.ci.pass / judged : null;
  return (
    <StatsRow className={cn("mt-2", denseStats)}>
      <Stat n={working} label={`${working === 1 ? "agent" : "agents"} with open changes`} icon={<PulseIcon size={16} />} />
      <Stat n={info ? <Link to={`${rp(repo)}/ci`}>{openCount(info.counts)}</Link> : "–"} label="open changes" icon={<GitPullRequestIcon size={16} />} />
      <Stat n={t.landed} label="landed" icon={<GitMergeIcon size={16} />} />
      <Stat
        n={
          t.reverted ? (
            <Link className="text-danger" to={`${rp(repo)}/ci?state=reverted`}>
              {t.reverted}
            </Link>
          ) : (
            0
          )
        }
        label="reverted after landing"
        icon={<HistoryIcon size={16} />}
      />
      <Stat n={t.rejected ? <Link to={`${rp(repo)}/ci?state=rejected`}>{t.rejected}</Link> : 0} label="rejected" icon={<XIcon size={16} />} />
      <Stat
        n={
          <Tooltip content={t.land_ms_p90 !== null ? `p90 ${duration(t.land_ms_p90)}` : "no lands in this window"}>
            <span>{t.land_ms_p50 !== null ? duration(t.land_ms_p50) : "–"}</span>
          </Tooltip>
        }
        label="median time to land"
        icon={<StopwatchIcon size={16} />}
      />
      <Stat
        n={
          <Tooltip content={`${s.ci.pass} passed, ${s.ci.fail} failed, ${s.ci.infra_error} infra errors; median job ${duration(s.ci.job_ms_p50) || "–"}`}>
            <span className={passRate === null ? undefined : rateTone(1 - passRate, 0.1, 0.25)}>{pct(passRate)}</span>
          </Tooltip>
        }
        label={s.ci.pending ? `CI pass rate · ${s.ci.pending} queued` : "CI pass rate"}
        icon={<PlayIcon size={16} />}
      />
    </StatsRow>
  );
}

const REVIEW_STALE_MS = 3_600_000;

/** The rates to watch as more agents land at once: review latency, train ejections, reverts, and who collides with whom. */
function Coordination({ repo, s }: { repo: string; s: RepoStats }) {
  const h = s.health;
  const r = h.review;
  const landed = s.throughput.landed;
  const reverted = s.throughput.reverted;
  const attempts = h.conflicts + landed;
  const trainRuns = h.train_ejected + landed;
  const stale = r.oldest_at !== null && Date.now() - r.oldest_at > REVIEW_STALE_MS;
  const incidents = [
    h.conflicts && plural(h.conflicts, "conflict"),
    h.train_ejected && plural(h.train_ejected, "train ejection"),
    reverted && plural(reverted, "revert"),
  ].filter(Boolean);
  const summary =
    !landed && !h.conflicts && !h.train_ejected ? "nothing landed in this window" : incidents.length ? incidents.join(", ") : "agents landed without colliding";
  return (
    <Card className={denseCard}>
      <CardHeader>
        <PulseIcon /> Coordination
        <span className="text-xs text-muted-foreground">{summary}</span>
      </CardHeader>
      <StatsRow className={cardStats}>
        <Stat
          n={
            r.waiting ? (
              <Link className={cn(stale && "text-warning")} to={`${rp(repo)}/ci?state=review`}>
                {r.waiting}
              </Link>
            ) : (
              0
            )
          }
          label={
            r.oldest_at ? (
              <>
                waiting for review · oldest <TimeAgo ms={r.oldest_at} />
              </>
            ) : (
              "waiting for review"
            )
          }
          icon={<ClockIcon size={16} />}
        />
        <Stat
          n={
            <Tooltip content={r.pickup_ms_p90 !== null ? `p90 ${duration(r.pickup_ms_p90)}` : "no reviews in this window"}>
              <span>{r.pickup_ms_p50 !== null ? duration(r.pickup_ms_p50) : "–"}</span>
            </Tooltip>
          }
          label={r.approved || r.rejected ? `median review pickup · ${r.approved} approved, ${r.rejected} rejected` : "median review pickup"}
          icon={<StopwatchIcon size={16} />}
        />
        <Stat
          n={
            <Tooltip
              content={`Changes that passed CI alone but failed combined with the changes ahead of them${h.parent_failed ? `; ${plural(h.parent_failed, "stacked change")} also rejected because a parent failed` : ""}`}
            >
              <span className={rateTone(h.train_fail_rate, 0.1, 0.25)}>{pct(h.train_fail_rate)}</span>
            </Tooltip>
          }
          label={trainRuns ? `merge train ejections · ${h.train_ejected} of ${trainRuns}` : "merge train ejections"}
          icon={<XIcon size={16} />}
        />
        <Stat
          n={
            reverted ? (
              <Link className={rateTone(h.revert_rate, 0.01, 0.1)} to={`${rp(repo)}/ci?state=reverted`}>
                {pct(h.revert_rate)}
              </Link>
            ) : (
              pct(h.revert_rate)
            )
          }
          label={landed ? `of lands reverted · ${reverted} of ${landed}` : "of lands reverted"}
          icon={<HistoryIcon size={16} />}
        />
        <Stat
          n={
            <Tooltip content="Land attempts that hit lines another change landed first; each one costs the agent a retry">
              <span className={rateTone(h.conflict_rate, 0.2, 0.4)}>{pct(h.conflict_rate)}</span>
            </Tooltip>
          }
          label={attempts ? `of land attempts conflicted · ${h.conflicts} of ${attempts}` : "of land attempts conflicted"}
          icon={<AlertFillIcon size={16} />}
        />
      </StatsRow>
      {h.conflict_pairs.length > 0 && <ConflictPairs pairs={h.conflict_pairs} />}
    </Card>
  );
}

/** Who keeps landing on whose lines: a pair that recurs usually means two agents share a task or a hot file. */
function ConflictPairs({ pairs }: { pairs: RepoStats["health"]["conflict_pairs"] }) {
  const max = Math.max(...pairs.map((p) => p.count));
  return (
    <div className="border-t">
      <div className="px-3 pt-2 text-xs font-semibold text-muted-foreground">Who conflicts with whom</div>
      <List className={cn("max-h-40 overflow-auto", denseList)}>
        {pairs.map((p) => (
          <ListItem key={`${p.agent}/${p.with_agent}`} className="items-center">
            <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
              <Avatar name={p.agent} size={16} />
              <b className="truncate">{p.agent}</b>
              <span className="shrink-0 text-muted-foreground">hit lines landed by</span>
              <Avatar name={p.with_agent} size={16} />
              <b className="truncate">{p.with_agent}</b>
            </span>
            <div className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-neutral-muted max-sm:hidden" aria-hidden>
              <div className="h-full rounded-full bg-warning-emphasis" style={{ width: `${(p.count / max) * 100}%` }} />
            </div>
            <Tooltip content={plural(p.count, "conflict")}>
              <Counter>{p.count}</Counter>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </div>
  );
}

/** What landing costs. bee never sees model tokens, so it counts what drives them: each `bee land` is an agent turn, each retry another. */
function Cost({ repo, s }: { repo: string; s: RepoStats }) {
  const c = s.cost;
  const landed = s.throughput.landed;
  return (
    <Card className={denseCard}>
      <CardHeader>
        <DatabaseIcon /> Cost
        <span className="text-xs text-muted-foreground">work spent per change on main</span>
      </CardHeader>
      <StatsRow className={cardStats}>
        <Stat
          n={
            <Tooltip
              content={`${plural(c.attempts, "land attempt")} for ${plural(landed, "land")}; every retry after a conflict or failed check is another agent turn`}
            >
              <span className={c.attempts_per_land === null ? undefined : rateTone(c.attempts_per_land - 1, 0.5, 1.5)}>
                {c.attempts_per_land !== null ? `${c.attempts_per_land.toFixed(1)}×` : "–"}
              </span>
            </Tooltip>
          }
          label="attempts per land"
          icon={<SyncIcon size={16} />}
        />
        <Stat
          n={c.discarded ? <Link to={`${rp(repo)}/ci?state=rejected`}>{c.discarded}</Link> : 0}
          label={c.attempts ? `discarded attempts · ${pct(c.discarded / c.attempts)}` : "discarded attempts"}
          icon={<XIcon size={16} />}
        />
        <Stat n={c.ci_ms ? duration(c.ci_ms) : "–"} label="CI runner time" icon={<StopwatchIcon size={16} />} />
        <Stat n={landed && c.ci_ms ? duration(Math.round(c.ci_ms / landed)) : "–"} label="CI time per land" icon={<GitMergeIcon size={16} />} />
        <Stat
          n={
            <Tooltip content="CI time on changes that were rejected, cancelled or reverted">
              <span className={c.ci_ms ? rateTone(c.discarded_ci_ms / c.ci_ms, 0.25, 0.5) : undefined}>{c.ci_ms ? pct(c.discarded_ci_ms / c.ci_ms) : "–"}</span>
            </Tooltip>
          }
          label="of CI time discarded"
          icon={<HistoryIcon size={16} />}
        />
      </StatsRow>
    </Card>
  );
}

function Lands({ s }: { s: RepoStats }) {
  const max = Math.max(1, ...s.series.map((p) => p.landed + p.reverted));
  const width = s.series.length > 1 ? s.series[1].ts - s.series[0].ts : 0;
  const when = (ms: number) =>
    new Date(ms).toLocaleString(undefined, width < 86_400_000 ? { hour: "numeric", minute: "2-digit" } : { month: "short", day: "numeric" });
  return (
    <Card className={denseCard}>
      <CardHeader>
        <GitMergeIcon /> Lands{" "}
        <span className="text-xs text-muted-foreground">
          {plural(s.throughput.landed, "land")}, {plural(s.throughput.reverted, "revert")}
        </span>
      </CardHeader>
      <CardContent className="p-3">
        <div className="flex h-20 items-end gap-0.5" role="img" aria-label="Lands and reverts over the window">
          {s.series.map((p) => (
            <Tooltip key={p.ts} content={`${when(p.ts)}: ${plural(p.landed, "land")}${p.reverted ? `, ${plural(p.reverted, "revert")}` : ""}`}>
              <div className="flex h-full flex-1 flex-col justify-end rounded-xs hover:bg-neutral-muted">
                {p.reverted > 0 && <div className="rounded-t-xs bg-danger-emphasis" style={{ height: `${(p.reverted / max) * 100}%` }} />}
                <div
                  className={cn("bg-success-emphasis", !p.reverted && "rounded-t-xs")}
                  style={{ height: `${(p.landed / max) * 100}%`, minHeight: p.landed ? 2 : 0 }}
                />
              </div>
            </Tooltip>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-xs text-faint">
          <span>{when(s.since)}</span>
          <span>now</span>
        </div>
      </CardContent>
    </Card>
  );
}

/** Batch titles read "bee #7: Turn CI…" or "bee batch #6: 3 changes…"; the number is already shown beside it. */
const batchTitle = (t: string) => t.replace(/^bee (batch )?#\d+:\s*/i, "") || t;

/** The batch PRs shipping lands to GitHub; hidden unless the repo has a remote in mode pr. */
function RemoteCard({ repo }: { repo: string }) {
  const st = useRemote(repo).data;
  if (!st?.remote) return null;
  const rem = st.remote;
  const f = forge(rem.provider);
  const batches = st.active ? [st.active, ...st.batches.filter((b) => b.n !== st.active?.n)] : st.batches;
  return (
    <Card className={denseCard}>
      <CardHeader>
        <RepoPushIcon /> Remote on {f.name}
        <span className="text-xs text-muted-foreground">
          {st.blocked ||
            (st.pending.lands === 0 ? (
              "nothing waiting to push"
            ) : (
              <>
                {plural(st.pending.lands, "land")} waiting
                {st.next_cut ? (
                  <>
                    , next batch <TimeAgo ms={st.next_cut} />
                  </>
                ) : null}
              </>
            ))}
          {rem.checked_at ? (
            <>
              {" "}
              · checked <TimeAgo ms={rem.checked_at} />
            </>
          ) : null}
        </span>
        <Link className="ml-auto text-xs font-normal" to={`${rp(repo)}/remote`}>
          View remote
        </Link>
      </CardHeader>
      {rem.error && (
        <div className="flex items-center gap-1.5 border-b px-3 py-1.5 text-xs text-warning">
          <AlertFillIcon size={12} />{" "}
          {rem.error_code === "conflict" ? `${f.name} and bee conflict; a batch ${f.short} is open for a human to resolve.` : "The sync hit an error."}{" "}
          <Link to={`${rp(repo)}/remote`}>Details</Link>
        </div>
      )}
      {batches.length ? (
        <List className={denseList}>
          {batches.slice(0, 5).map((b) => (
            <ListItem key={b.n} className="min-h-9">
              <BatchLabel state={b.state} />
              <ListItemTitle className="min-w-0 flex-1 truncate font-medium">{batchTitle(b.title)}</ListItemTitle>
              <ListItemMeta className="mt-0 hidden shrink-0 flex-nowrap sm:flex">
                <span className="font-mono">#{b.n}</span>
                <ListItemSeparator />
                <span>{plural(b.lands, "land")}</span>
                <ListItemSeparator />
                <Sha repo={repo} sha={b.merge_sha ?? b.head_sha} n={7} />
                <ListItemSeparator />
                <TimeAgo ms={b.updated} />
              </ListItemMeta>
              <Badge>
                <PrLink b={b} provider={rem.provider} />
              </Badge>
            </ListItem>
          ))}
        </List>
      ) : (
        <Empty icon={GitPullRequestIcon}>No batch {f.short}s yet. bee opens one once lands are waiting.</Empty>
      )}
    </Card>
  );
}

function InFlight({ repo }: { repo: string }) {
  const q = useChanges(repo, "open");
  return (
    <Card className={denseCard}>
      <CardHeader>
        <GitPullRequestIcon /> In flight
        <span className="text-xs text-muted-foreground">changes agents are landing now</span>
        <Link className="ml-auto text-xs font-normal" to={`${rp(repo)}/ci`}>
          View all
        </Link>
      </CardHeader>
      <Query q={q}>
        {(d) =>
          d.changes.length ? (
            <List className={denseList}>
              {d.changes.slice(0, 10).map((c) => (
                <ChangeRow key={c.id} repo={repo} c={c} />
              ))}
            </List>
          ) : (
            <Empty icon={GitPullRequestIcon} bee="idle">
              No open changes. Agents open them with <code>bee land</code>.
            </Empty>
          )
        }
      </Query>
    </Card>
  );
}
